// Measuring with informal units (Year 2 pictures, 22 Oct 2026):
//
// - `cups`: containers, each with the cups (or spoons) of water that fill
//   it, or a container marked in equal cup-sized parts with water in it;
// - `unit_line`: a thing such as a crayon, with paper clips or cubes laid
//   along under it;
// - `balance`: a two-pan balance with things or cubes on each pan, the beam
//   level or tipped down on the heavier side.
//
// Year 2 has no formal units: no cm, m, kg, g, L or mL is ever printed or
// spoken. How many cups, clips or cubes there are, how full a container is,
// how the units are laid and which way the beam tips are drawn, never
// written or spoken: reading them is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const CONTAINERS = [
  'bowl',
  'bottle',
  'vase',
  'bucket',
  'pot',
  'glass',
  'jar',
] as const;

// Only containers with straight up-and-down sides can be marked in equal
// parts: on a curved or sloping one, equal heights do not hold the same.
export const MARKED_CONTAINERS = ['bottle', 'jar', 'pot'] as const;

export const LINE_OBJECTS = [
  'pencil',
  'crayon',
  'ribbon',
  'rope',
  'book',
  'straw',
] as const;

export const LENGTH_UNITS = ['paper_clip', 'cube'] as const;

// Units are counted, so they are never counted out loud; objects are given.
export const BALANCE_UNITS = ['cube', 'marble'] as const;
export const BALANCE_OBJECTS = [
  'apple',
  'teddy',
  'car',
  'book',
  'ball',
  'pencil',
] as const;

export type Container = (typeof CONTAINERS)[number];
export type ContainerHeight = 'short' | 'medium' | 'tall';
export type LineObject = (typeof LINE_OBJECTS)[number];
export type LengthUnit = (typeof LENGTH_UNITS)[number];
export type BalanceUnit = (typeof BALANCE_UNITS)[number];
export type BalanceObject = (typeof BALANCE_OBJECTS)[number];
export type BalanceItem = BalanceUnit | BalanceObject;

export type FilledContainer = {
  container: Container;
  height: ContainerHeight;
  // How many cups or spoons fill it, all drawn under it.
  count: number;
};

export type MarkedContainer = {
  container: (typeof MARKED_CONTAINERS)[number];
  height: ContainerHeight;
  // How many equal cup-sized parts it is marked in, and how many hold water.
  marks: number;
  filled: number;
};

export type CupsSpec =
  | {
      type: 'cups';
      unit: 'cup' | 'spoon';
      show: 'filled_by';
      containers: FilledContainer[];
    }
  | {
      type: 'cups';
      unit: 'cup' | 'spoon';
      show: 'marked';
      containers: MarkedContainer[];
    };

export type UnitLineSpec = {
  type: 'unit_line';
  object: LineObject;
  unit: LengthUnit;
  count: number;
  // End to end is the right way; gaps and overlaps are for spotting a
  // mistake.
  lay: 'end_to_end' | 'gaps' | 'overlap';
};

export type PanEntry = { item: BalanceItem; count: number };

export type BalanceSpec = {
  type: 'balance';
  left: PanEntry[];
  right: PanEntry[];
  // Which way the beam sits: the heavier side goes down.
  tilt: 'level' | 'left_down' | 'right_down';
};

export type InformalMeasureSpec = CupsSpec | UnitLineSpec | BalanceSpec;

const MAX_CONTAINERS = 3;
const MAX_CUPS = 12;
const MAX_MARKS = 8;
const MAX_UNITS_IN_LINE = 12;
const MAX_ON_PAN = 20;
const MAX_OBJECTS = 4;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

function isOneOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return (list as readonly unknown[]).includes(value);
}

// How tall each container is drawn when nothing else is asked for.
const USUAL_HEIGHT: Record<Container, ContainerHeight> = {
  bowl: 'short',
  bottle: 'medium',
  vase: 'tall',
  bucket: 'medium',
  pot: 'short',
  glass: 'medium',
  jar: 'medium',
};

export function readCups(record: Record<string, unknown>): CupsSpec | null {
  const unit = record.unit ?? 'cup';
  if (unit !== 'cup' && unit !== 'spoon') return null;
  const { containers } = record;
  if (!Array.isArray(containers)) return null;
  if (containers.length < 1 || containers.length > MAX_CONTAINERS) {
    return null;
  }
  const filled: FilledContainer[] = [];
  const marked: MarkedContainer[] = [];
  for (const entry of containers as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const box = entry as Record<string, unknown>;
    if (!isOneOf(CONTAINERS, box.container)) return null;
    const height = box.height ?? USUAL_HEIGHT[box.container];
    if (height !== 'short' && height !== 'medium' && height !== 'tall') {
      return null;
    }
    if (box.count !== undefined) {
      if (box.marks !== undefined || box.filled !== undefined) return null;
      if (!isWholeIn(box.count, 1, MAX_CUPS)) return null;
      filled.push({
        container: box.container,
        height,
        count: box.count as number,
      });
    } else {
      if (!isOneOf(MARKED_CONTAINERS, box.container)) return null;
      if (!isWholeIn(box.marks, 2, MAX_MARKS)) return null;
      const marks = box.marks as number;
      if (!isWholeIn(box.filled, 1, marks)) return null;
      marked.push({
        container: box.container,
        height,
        marks,
        filled: box.filled as number,
      });
    }
  }
  // All containers are shown the same way.
  if (filled.length > 0 && marked.length > 0) return null;
  return filled.length > 0
    ? { type: 'cups', unit, show: 'filled_by', containers: filled }
    : { type: 'cups', unit, show: 'marked', containers: marked };
}

export function readUnitLine(
  record: Record<string, unknown>,
): UnitLineSpec | null {
  const { object, count } = record;
  const unit = record.unit ?? 'paper_clip';
  const lay = record.lay ?? 'end_to_end';
  if (!isOneOf(LINE_OBJECTS, object) || !isOneOf(LENGTH_UNITS, unit)) {
    return null;
  }
  if (lay !== 'end_to_end' && lay !== 'gaps' && lay !== 'overlap') {
    return null;
  }
  // A gap or an overlap needs two units to show.
  const fewest = lay === 'end_to_end' ? 1 : 2;
  if (!isWholeIn(count, fewest, MAX_UNITS_IN_LINE)) return null;
  return { type: 'unit_line', object, unit, count: count as number, lay };
}

function readPan(value: unknown): PanEntry[] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > 3) {
    return null;
  }
  const read: PanEntry[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const { item, count } = entry as Record<string, unknown>;
    const most = isOneOf(BALANCE_UNITS, item)
      ? MAX_ON_PAN
      : isOneOf(BALANCE_OBJECTS, item)
        ? MAX_OBJECTS
        : 0;
    if (!isWholeIn(count, 1, most)) return null;
    // Each kind once per pan, so its things sit together.
    if (read.some((other) => other.item === item)) return null;
    read.push({ item: item as BalanceItem, count: count as number });
  }
  const pieces = read.reduce((sum, entry) => sum + entry.count, 0);
  return pieces > MAX_ON_PAN ? null : read;
}

export function readBalance(
  record: Record<string, unknown>,
): BalanceSpec | null {
  const left = readPan(record.left);
  const right = readPan(record.right);
  const { tilt } = record;
  if (left === null || right === null) return null;
  if (tilt !== 'level' && tilt !== 'left_down' && tilt !== 'right_down') {
    return null;
  }
  return { type: 'balance', left, right, tilt };
}

const UNIT_PLURAL: Record<'cup' | 'spoon' | LengthUnit | BalanceUnit, string> =
  {
    cup: 'cups',
    spoon: 'spoons',
    paper_clip: 'paper clips',
    cube: 'cubes',
    marble: 'marbles',
  };

const OBJECT_WORDS: Record<BalanceObject, [string, string]> = {
  apple: ['an apple', 'apples'],
  teddy: ['a teddy', 'teddies'],
  car: ['a toy car', 'toy cars'],
  book: ['a book', 'books'],
  ball: ['a ball', 'balls'],
  pencil: ['a pencil', 'pencils'],
};

// The word printed under an object on a pan, and under a container.
export const OBJECT_NAMES: Record<BalanceObject, string> = {
  apple: 'apple',
  teddy: 'teddy',
  car: 'toy car',
  book: 'book',
  ball: 'ball',
  pencil: 'pencil',
};

function list(words: string[]): string {
  return words.length < 2
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

function article(word: string): string {
  return /^[aeiou]/.test(word) ? `an ${word}` : `a ${word}`;
}

// What a screen reader is told in place of each picture. Never how many
// cups, spoons, clips, cubes or marbles there are, never how full a
// container is, never how the units are laid and never which way the beam
// tips.
export function describeCups(spec: CupsSpec): string {
  const names = list(spec.containers.map((box) => article(box.container)));
  const one = spec.containers.length === 1;
  const sentence = `${names.charAt(0).toUpperCase()}${names.slice(1)}`;
  if (spec.show === 'filled_by') {
    return one
      ? `${sentence}, and the ${UNIT_PLURAL[spec.unit]} of water that fill it.`
      : `${sentence}. Under each are the ${UNIT_PLURAL[spec.unit]} of water that fill it.`;
  }
  return one
    ? `${sentence} marked in equal parts. Each part holds one ${spec.unit}. There is water in it.`
    : `${sentence}, each marked in equal parts. Each part holds one ${spec.unit}. There is water in each.`;
}

export function describeUnitLine(spec: UnitLineSpec): string {
  const thing = article(spec.object);
  return `${thing.charAt(0).toUpperCase()}${thing.slice(1)}, with ${UNIT_PLURAL[spec.unit]} laid along under it.`;
}

function sayPan(entries: PanEntry[]): string {
  return list(
    entries.map((entry) => {
      if (isOneOf(BALANCE_UNITS, entry.item)) return UNIT_PLURAL[entry.item];
      const [one, many] = OBJECT_WORDS[entry.item];
      return entry.count === 1 ? one : `${String(entry.count)} ${many}`;
    }),
  );
}

export function describeBalance(spec: BalanceSpec): string {
  return `A balance with two pans. On the left pan: ${sayPan(spec.left)}. On the right pan: ${sayPan(spec.right)}.`;
}

export function describeInformalMeasure(spec: InformalMeasureSpec): string {
  switch (spec.type) {
    case 'cups':
      return describeCups(spec);
    case 'unit_line':
      return describeUnitLine(spec);
    case 'balance':
      return describeBalance(spec);
  }
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export const INFORMAL_LABEL_SIZE = 13;
const EDGE = 8;
const MAX_WIDTH = 340;

function round(value: number): number {
  return Number(value.toFixed(2));
}

const CONTAINER_HEIGHTS: Record<ContainerHeight, number> = {
  short: 64,
  medium: 96,
  tall: 128,
};

const CONTAINER_WIDTHS: Record<Container, number> = {
  bowl: 90,
  bottle: 50,
  vase: 56,
  bucket: 76,
  pot: 84,
  glass: 46,
  jar: 60,
};

export type CupsColumn = {
  container: Container;
  // The box the container is drawn in; all containers stand on one line.
  box: { x: number; y: number; width: number; height: number };
  // The top of the water inside: the full line, or the top filled part.
  waterTop: number;
  // The full line of a marked container, and where its marks are drawn.
  marks: number[];
  cups: { x: number; y: number }[];
  name: { x: number; y: number };
  card: { x: number; y: number; width: number; height: number };
};

export type CupsPicture = {
  columns: CupsColumn[];
  cupSize: number;
  width: number;
  height: number;
};

const COLUMN = 100;
const COLUMN_GAP = 12;
const CUP_SIZE = 17;
const CUP_STEP = 20;

// Where the water line of a full container sits, below its rim or neck.
export function fullLine(container: Container, top: number, height: number) {
  if (container === 'bottle') return top + height * 0.32;
  if (container === 'vase') return top + height * 0.2;
  if (container === 'jar') return top + 12;
  return top + Math.min(10, height * 0.12);
}

export function cupsPicture(spec: CupsSpec): CupsPicture {
  const tallest = Math.max(
    ...spec.containers.map((box) => CONTAINER_HEIGHTS[box.height]),
  );
  const base = EDGE + 10 + tallest;
  const mostCups =
    spec.show === 'filled_by'
      ? Math.max(...spec.containers.map((box) => box.count))
      : 0;
  const cupRows = Math.ceil(mostCups / 5);
  const nameY = base + 16;
  const cupsTop = nameY + 16;
  const cardBottom =
    cupRows === 0 ? nameY + 14 : cupsTop + cupRows * CUP_STEP + 4;
  const columns = spec.containers.map((box, index): CupsColumn => {
    const left = EDGE + index * (COLUMN + COLUMN_GAP);
    const middle = left + COLUMN / 2;
    const width = CONTAINER_WIDTHS[box.container];
    const height = CONTAINER_HEIGHTS[box.height];
    const top = base - height;
    // A marked pot is full to its rim, so its top mark is the rim itself: a
    // strip left empty above the marks read as one more part in the Year 2
    // image check (23 Oct 2026).
    const full =
      'marks' in box && box.container === 'pot'
        ? top
        : fullLine(box.container, top, height);
    let waterTop = full;
    const marks: number[] = [];
    if ('marks' in box) {
      const part = (base - 3 - full) / box.marks;
      for (let at = 1; at <= box.marks; at += 1) {
        marks.push(round(base - 3 - at * part));
      }
      waterTop = round(base - 3 - box.filled * part);
    }
    const count = 'count' in box ? box.count : 0;
    const across = Math.min(count, 5);
    const cupsLeft = middle - (across * CUP_STEP - (CUP_STEP - CUP_SIZE)) / 2;
    return {
      container: box.container,
      box: { x: middle - width / 2, y: top, width, height },
      waterTop,
      marks,
      cups: Array.from({ length: count }, (_, at) => ({
        x: round(cupsLeft + (at % 5) * CUP_STEP),
        y: cupsTop + Math.floor(at / 5) * CUP_STEP,
      })),
      name: { x: middle, y: nameY },
      card: { x: left, y: EDGE, width: COLUMN, height: cardBottom - EDGE },
    };
  });
  return {
    columns,
    cupSize: CUP_SIZE,
    width:
      EDGE * 2 +
      spec.containers.length * COLUMN +
      (spec.containers.length - 1) * COLUMN_GAP,
    height: cardBottom + EDGE,
  };
}

export type UnitLinePicture = {
  // The thing being measured, from one x to the other.
  object: { from: number; to: number; y: number; height: number };
  units: { x: number; y: number; length: number; height: number }[];
  width: number;
  height: number;
};

const UNIT_LENGTHS: Record<LengthUnit, number> = { paper_clip: 32, cube: 24 };
const OBJECT_HEIGHTS: Record<LineObject, number> = {
  pencil: 18,
  crayon: 20,
  ribbon: 18,
  rope: 14,
  book: 30,
  straw: 10,
};

export function unitLinePicture(spec: UnitLineSpec): UnitLinePicture {
  const { count, lay } = spec;
  // How far each unit starts from the one before, as a share of its length.
  const pitch = lay === 'gaps' ? 1.45 : lay === 'overlap' ? 0.62 : 1;
  const span = (count - 1) * pitch + 1;
  const length = round(
    Math.min(UNIT_LENGTHS[spec.unit], (MAX_WIDTH - EDGE * 2) / span),
  );
  const unitHeight =
    spec.unit === 'cube' ? Math.min(length, 24) : round(length * 0.38);
  const objectHeight = OBJECT_HEIGHTS[spec.object];
  const unitsTop = EDGE + objectHeight + 12;
  const units = Array.from({ length: count }, (_, at) => ({
    x: round(EDGE + at * pitch * length),
    // Overlapping units take turns up and down, so each one shows.
    y: lay === 'overlap' && at % 2 === 1 ? unitsTop + 6 : unitsTop,
    length,
    height: unitHeight,
  }));
  const to = round(EDGE + span * length);
  return {
    object: { from: EDGE, to, y: EDGE, height: objectHeight },
    units,
    width: Math.max(to + EDGE, 120),
    height: unitsTop + unitHeight + (lay === 'overlap' ? 6 : 0) + EDGE,
  };
}

export type PanPiece = {
  item: BalanceItem;
  x: number;
  y: number;
  size: number;
};

export type BalancePan = {
  // The middle of the pan's top, where the things sit.
  x: number;
  y: number;
  width: number;
  pieces: PanPiece[];
  // The names of the objects on it, printed under the pan, one a line.
  names: { text: string; x: number; y: number }[];
};

export type BalancePicture = {
  pivot: { x: number; y: number };
  beam: { x1: number; y1: number; x2: number; y2: number };
  // How far the beam is turned, in degrees: down on the left is negative.
  angle: number;
  left: BalancePan;
  right: BalancePan;
  base: { y: number };
  width: number;
  height: number;
};

const PAN_WIDTH = 108;
const HALF_BEAM = 110;
const TILT = 12;
const UNIT_SIZE = 17;
const UNIT_STEP = 19;
const OBJECT_SIZE = 40;
const OBJECT_STEP = 44;
const NAME_LINE = 15;
// How much of its square each object fills, from the bottom up.
const DRAWN_HEIGHT: Partial<Record<BalanceObject, number>> = {
  car: 0.62,
  book: 0.45,
  pencil: 0.3,
};

// The things on a pan, from the pan upwards: each kind in its own block,
// units in rows of five and objects in rows of two.
function stack(entries: PanEntry[]): {
  pieces: { item: BalanceItem; dx: number; dy: number; size: number }[];
  height: number;
} {
  const pieces: { item: BalanceItem; dx: number; dy: number; size: number }[] =
    [];
  let rise = 0;
  for (const entry of entries) {
    const unit = isOneOf(BALANCE_UNITS, entry.item);
    const size = unit ? UNIT_SIZE : OBJECT_SIZE;
    const step = unit ? UNIT_STEP : OBJECT_STEP;
    // Flat things are stacked by how tall they are drawn, not by the square
    // they are drawn in, so nothing floats above them.
    const rowStep = unit
      ? UNIT_STEP
      : Math.round(
          OBJECT_SIZE * (DRAWN_HEIGHT[entry.item as BalanceObject] ?? 1),
        ) + 4;
    const perRow = unit ? 5 : 2;
    const rows = Math.ceil(entry.count / perRow);
    for (let at = 0; at < entry.count; at += 1) {
      const row = Math.floor(at / perRow);
      const inRow = Math.min(perRow, entry.count - row * perRow);
      const column = at % perRow;
      pieces.push({
        item: entry.item,
        dx: (column - (inRow - 1) / 2) * step - size / 2,
        dy: -(rise + row * rowStep + size),
        size,
      });
    }
    rise += rows * rowStep;
  }
  const top = Math.max(0, ...pieces.map((piece) => -piece.dy));
  return { pieces, height: top };
}

export function balancePicture(spec: BalanceSpec): BalancePicture {
  const left = stack(spec.left);
  const right = stack(spec.right);
  const drop = HALF_BEAM * Math.sin((TILT * Math.PI) / 180);
  const reach = round(HALF_BEAM * Math.cos((TILT * Math.PI) / 180));
  const pivot = {
    x: MAX_WIDTH / 2,
    y: round(EDGE + Math.max(left.height, right.height) + drop + 14),
  };
  const angle =
    spec.tilt === 'level' ? 0 : spec.tilt === 'left_down' ? -TILT : TILT;
  const leftDrop = angle === 0 ? 0 : angle < 0 ? drop : -drop;
  const ends = {
    x1: angle === 0 ? pivot.x - HALF_BEAM : pivot.x - reach,
    y1: round(pivot.y + leftDrop),
    x2: angle === 0 ? pivot.x + HALF_BEAM : pivot.x + reach,
    y2: round(pivot.y - leftDrop),
  };
  const pan = (
    x: number,
    y: number,
    entries: PanEntry[],
    pile: ReturnType<typeof stack>,
  ): BalancePan => ({
    x,
    y: y - 6,
    width: PAN_WIDTH,
    pieces: pile.pieces.map((piece) => ({
      item: piece.item,
      x: round(x + piece.dx),
      y: round(y - 6 + piece.dy),
      size: piece.size,
    })),
    names: entries
      .map((entry) => entry.item)
      .filter((item): item is BalanceObject => isOneOf(BALANCE_OBJECTS, item))
      .map((item, line) => ({
        text: OBJECT_NAMES[item],
        x,
        y: round(y + 18 + line * NAME_LINE),
      })),
  });
  const lines = Math.max(
    ...[spec.left, spec.right].map(
      (entries) =>
        entries.filter((entry) => isOneOf(BALANCE_OBJECTS, entry.item)).length,
    ),
  );
  // The stand reaches below the lower pan and the names under it.
  const baseY = round(pivot.y + drop + 24 + lines * NAME_LINE);
  return {
    pivot,
    beam: ends,
    angle,
    left: pan(ends.x1, ends.y1, spec.left, left),
    right: pan(ends.x2, ends.y2, spec.right, right),
    base: { y: baseY },
    width: MAX_WIDTH,
    height: baseY + 14 + EDGE,
  };
}
